import { useCallback, useEffect, useState } from 'react';
import { addAlert } from 'documents-lib/js/utils/addAlert';
import { generateDocumentDescription, fetchDocumentDescriptionCHIRP, fetchDocumentDescriptionREST, updateDocumentDescription, submitDocumentDescriptionFeedback } from 'documents-ui/api/DocumentDescriptionApi';
import { isUngatedToCHIRPEndpoints } from 'documents-ui/utils/gates';
import { useUpdateDocumentSummaryMutation, useSummarizeDocumentMutation, useSubmitFeedbackMutation } from '../../../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentsSummarizationService.hooks';
const START_PAGE_NUMBER = 1;
const MAX_NUMBER_OF_CHARACTERS = 400;
const MINIMUM_ANIMATION_LENGTH = 400;
export const useDocumentDescription = ({
  deckId
}) => {
  const [description, setDescription] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [fetchLoading, setFetchLoading] = useState(true);
  const [generateLoadingREST, setGenerateLoadingREST] = useState(false);
  const [isUserEdited, setIsUserEdited] = useState(false);
  const [feedbackDisabled, setFeedbackDisabled] = useState(true);
  const [updateSummaryMutation, {
    loading: updateLoading
  }] = useUpdateDocumentSummaryMutation();
  const [summarizeMutation, {
    loading: generateLoadingCHIRP
  }] = useSummarizeDocumentMutation();
  const [submitFeedbackMutation] = useSubmitFeedbackMutation();
  const isUngatedToChirp = isUngatedToCHIRPEndpoints();
  const disableLoading = useCallback(() => {
    setTimeout(() => {
      setFetchLoading(false);
    }, MINIMUM_ANIMATION_LENGTH);
  }, []);

  // Loading the stored description
  useEffect(() => {
    if (isUngatedToChirp) {
      fetchDocumentDescriptionCHIRP({
        deckId
      }).then(result => {
        if (result.documentSummary) {
          setDescription(result.documentSummary.text);
          setIsUserEdited(result.documentSummary.isUserEdited);
        }
        disableLoading();
      }).catch(err => {
        setFetchLoading(false);
        console.error(err);
      });
    } else {
      fetchDocumentDescriptionREST({
        deckId
      }).then(result => {
        if (result.deckSummary) {
          setDescription(result.deckSummary.text);
          setIsUserEdited(result.deckSummary.isUserEdited);
        }
        disableLoading();
      }).catch(err => {
        setFetchLoading(false);
        console.error(err);
      });
    }
  }, [deckId, disableLoading, isUngatedToChirp]);
  const saveDescription = useCallback(async (text, userEdited) => {
    if (isUngatedToChirp) {
      try {
        var _result$data, _result$data$result$d, _result$data2;
        const result = await updateSummaryMutation({
          variables: {
            documentId: deckId,
            text
          }
        });
        const summaryText = (_result$data = result.data) === null || _result$data === void 0 || (_result$data = _result$data.result.documentSummary) === null || _result$data === void 0 ? void 0 : _result$data.text;
        const userEditedValue = (_result$data$result$d = (_result$data2 = result.data) === null || _result$data2 === void 0 || (_result$data2 = _result$data2.result.documentSummary) === null || _result$data2 === void 0 ? void 0 : _result$data2.isUserEdited) !== null && _result$data$result$d !== void 0 ? _result$data$result$d : false;
        if (summaryText) {
          setDescription(summaryText);
          setIsUserEdited(userEditedValue);
          return summaryText;
        }
        return null;
      } catch (err) {
        addAlert('saveDocumentDescription', 'danger', {});
        console.error(err);
        return null;
      }
    }
    return updateDocumentDescription({
      deckId,
      text,
      isUserEdited: userEdited
    }).then(result => {
      setDescription(result.text);
      setIsUserEdited(result.isUserEdited);
      return result.text;
    }).catch(err => {
      addAlert('saveDocumentDescription', 'danger', {});
      console.error(err);
      return null;
    });
  }, [deckId, updateSummaryMutation, isUngatedToChirp]);
  const generateDescription = useCallback(async () => {
    setFeedbackDisabled(true);
    if (isUngatedToChirp) {
      try {
        var _result$data3;
        const result = await summarizeMutation({
          variables: {
            documentId: deckId,
            summarizationVersion: 3,
            summarizationOptions: {
              startPageNumber: START_PAGE_NUMBER,
              maxNumberOfOutputCharacters: MAX_NUMBER_OF_CHARACTERS
            }
          }
        });
        const summaryData = (_result$data3 = result.data) === null || _result$data3 === void 0 || (_result$data3 = _result$data3.result) === null || _result$data3 === void 0 ? void 0 : _result$data3.summary;
        if (summaryData !== null && summaryData !== void 0 && summaryData.error) {
          throw new Error(summaryData.error);
        }
        if (!(summaryData !== null && summaryData !== void 0 && summaryData.text) || summaryData.text === '') {
          throw new Error('The generated description is empty');
        }
        if (summaryData.correlationId) {
          setCorrelationId(summaryData.correlationId);
          setFeedbackDisabled(false);
        }
        return summaryData.text;
      } catch (err) {
        console.error(err);
        return null;
      }
    }
    setGenerateLoadingREST(true);
    return generateDocumentDescription({
      deckId,
      options: {
        summarizationVersion: 3,
        summarizationOptions: {
          startPageNumber: START_PAGE_NUMBER,
          maxNumberOfOutputCharacters: MAX_NUMBER_OF_CHARACTERS
        }
      }
    }).then(result => {
      if (result.summary.error) {
        throw new Error(result.summary.error);
      }
      if (result.summary.text === '') {
        throw new Error('The generated description is empty');
      }
      if (result.summary.correlationId) {
        setCorrelationId(result.summary.correlationId);
        setFeedbackDisabled(false);
      }
      setGenerateLoadingREST(false);
      return result.summary.text;
    }).catch(err => {
      setGenerateLoadingREST(false);
      console.error(err);
      return null;
    });
  }, [deckId, summarizeMutation, isUngatedToChirp]);
  const submitDescriptionFeedback = (feedbackData, isFeedbackForm = false) => {
    if (isUngatedToChirp) {
      var _feedbackData$feedbac;
      submitFeedbackMutation({
        variables: {
          correlationId: feedbackData.correlationId,
          vote: feedbackData.vote,
          feedbackGeneric: feedbackData.selectedFormOptions,
          feedbackFreeform: (_feedbackData$feedbac = feedbackData.feedbackMessage) !== null && _feedbackData$feedbac !== void 0 ? _feedbackData$feedbac : ''
        }
      }).then(() => {
        if (isFeedbackForm) {
          addAlert('submitFeedback', 'success', {});
        }
      }).catch(() => {
        addAlert('submitFeedback', 'danger', {});
        console.error('Failed to send feedback');
      });
    } else {
      submitDocumentDescriptionFeedback(feedbackData).then(() => {
        if (isFeedbackForm) {
          addAlert('submitFeedback', 'success', {});
        }
      }).catch(() => {
        addAlert('submitFeedback', 'danger', {});
        console.error('Failed to send feedback');
      });
    }
  };
  const generateLoading = isUngatedToChirp ? generateLoadingCHIRP : generateLoadingREST;
  const isLoading = fetchLoading || generateLoading || updateLoading;
  return {
    correlationId,
    description,
    feedbackDisabled,
    isLoading,
    isUserEdited,
    saveDescription,
    generateDescription,
    submitDescriptionFeedback,
    setFeedbackDisabled
  };
};